import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import LogoutIcon from '@mui/icons-material/Logout'
import MenuIcon from '@mui/icons-material/Menu'
import { AppBar, Badge, Box, Button, IconButton, Menu, MenuItem, Toolbar, Tooltip, Typography } from '@mui/material'
import { styled } from '@mui/styles'
import React, { useState } from 'react'

import { useKeycloak } from '../../hooks/keycloakContextProvider'
import { displayHighestRole } from '../../types/keycloakUsers'
import { standardErrorMessage } from '../../utils/constants/constants'
import { useChatContext } from '../chat/ChatContext'

const DashboardNavbarRoot = styled(AppBar)(() => ({
    paddingRight: '0px',
}))

interface NavbarProps {
    handleSidebarToggle: () => void
}

export const DashboardNavbar = ({ handleSidebarToggle }: NavbarProps) => {
    const { username, logout, roles } = useKeycloak()
    const { isOpen: isChatOpen, toggleOpen: toggleChat, unreadCount } = useChatContext()
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)
    const handleClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)
    const handleClose = () => setAnchorEl(null)

    const handleLogout = () => {
        logout()
    }

    return (
        <>
            <DashboardNavbarRoot
                sx={{
                    width: {
                        lg: '100%',
                    },
                    borderLeft: 1,
                    backgroundColor: '#ffffff',
                    boxShadow: '1px 1px 6px rgba(0, 0, 0, 0.3)',
                }}
            >
                <Toolbar disableGutters sx={{ height: 72, left: 0 }}>
                    <IconButton data-test="sidebar-toggle-button" onClick={handleSidebarToggle}>
                        <MenuIcon fontSize="medium" sx={{ marginLeft: '20px', color: 'rgba(33, 36, 43, 1)' }} />
                    </IconButton>
                    <Badge
                        badgeContent={unreadCount}
                        invisible={unreadCount === 0 || isChatOpen}
                        data-test="chat-toggle-badge"
                        sx={{
                            position: 'absolute',
                            right: 274,
                            '& .MuiBadge-badge': {
                                backgroundColor: '#e04e6a',
                                color: '#fff',
                                fontWeight: 700,
                            },
                        }}
                    >
                        <Button
                            data-test="chat-toggle-button"
                            onClick={toggleChat}
                            aria-label="Toggle chat assistant"
                            variant={isChatOpen ? 'contained' : 'outlined'}
                            disableElevation
                            startIcon={<ChatBubbleOutlineIcon />}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: '8px',
                            }}
                        >
                            Assistant
                        </Button>
                    </Badge>
                    <Box
                        onClick={handleClick}
                        data-test="inner"
                        sx={{
                            position: 'absolute',
                            right: 0,
                            display: 'flex',
                            alignItems: 'center',
                            borderLeft: '1px solid rgba(175, 175, 175, 1)',
                            width: '250px',
                            height: '100%',
                            ':hover': {
                                cursor: 'pointer',
                            },
                        }}
                    >
                        <AccountCircleIcon
                            fontSize="large"
                            sx={{ color: 'rgba(33, 36, 43, 1)', mr: 1, marginLeft: '8px' }}
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0 }}>
                            <Typography sx={{ color: 'rgba(98, 98, 98, 1)', fontSize: '14px', fontWeight: 400 }}>
                                My Account
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography
                                    data-test="username-typography"
                                    sx={{ color: 'rgba(14, 14, 14, 1)', fontSize: '14px', fontWeight: 700, mr: 1 }}
                                >
                                    {username ? username : 'User'}
                                </Typography>
                                <KeyboardArrowDownIcon sx={{ color: 'rgba(0, 111, 214, 1)' }} />
                            </Box>
                        </Box>
                    </Box>
                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        slotProps={{
                            paper: {
                                style: {
                                    minWidth: '230px',
                                    borderRadius: 0,
                                    backgroundColor: '#ffffff',
                                    border: '1px solid rgba(175, 175, 175, 1)',
                                    borderTop: '0.5px solid rgba(175, 175, 175, 0.5)',
                                },
                            },
                        }}
                    >
                        <Typography
                            data-test="user-role-typography"
                            sx={{
                                fontWeight: 400,
                                color: 'rgba(98, 98, 98, 1)',
                                marginLeft: '16px',
                                marginBottom: '4px',
                                curser: 'default',
                            }}
                        >
                            Role: {displayHighestRole(roles)}
                            <Tooltip data-test="role-tooltip" title={`${standardErrorMessage} to change your role`}>
                                <HelpOutlineIcon fontSize="inherit" />
                            </Tooltip>
                        </Typography>

                        <MenuItem
                            onClick={handleLogout}
                            sx={{
                                marginTop: '4px',
                                borderTop: '0.5px solid rgba(98, 98, 98, 1)',
                                ':hover': { color: 'rgba(0, 111, 214, 1)', backgroundColor: '#ffffff' },
                            }}
                        >
                            <LogoutIcon fontSize="small" />
                            Logout
                        </MenuItem>
                    </Menu>
                </Toolbar>
            </DashboardNavbarRoot>
        </>
    )
}
